/* eslint-disable no-nested-ternary */
import React, { useState } from 'react';
import { useIntl } from 'react-intl';
import RedirectHandler from '../../components/RedirectHandler';
import Typography from '../../components/Typography';
import { Box } from '../../components/Box';
import { L10n } from '../../components/L10n';
import Button from '../../components/Button';
import { RadioGroup } from '../../components/Radio';
import { useClientStore } from '../../store/store';
import PageNavigation from '../../components/PageNavigation';
import { pregnancyRestrictedVaccines } from '../../utils/vaccineSpecifications/pregnancyRestrictedVaccines';
import { removeSpecificVaccines } from '../../utils/removeSpecificVaccines';

const IsPregnant = () => {
  const intl = useIntl();
  const {
    apptState,
    patientIsPregnant,
    updateApptState,
    updatePatientStore,
    updateUiState,
  } = useClientStore((state) => ({
    apptState: state.apptDetails,
    patientIsPregnant: state.patient.isPregnant,
    updateApptState: state.updateApptDetails,
    updatePatientStore: state.updatePatientInfo,
    updateUiState: state.updateUiState,
  }));
  const selectedVaccines = apptState.products;
  const [inputState, setInputState] = useState({
    isPregnant: { value: patientIsPregnant, hasError: false, error: '' },
  });

  const restrictedVaccinesSelected = () => {
    const hasRestricted = Object.keys(selectedVaccines).some(
      (vax) => vax in pregnancyRestrictedVaccines
    );
    const allRestricted = Object.keys(selectedVaccines).every((vax) => {
      return vax in pregnancyRestrictedVaccines;
    });
    return {
      hasRestricted,
      allRestricted,
    };
  };

  const selectedVaccineKeys = Object.keys(selectedVaccines);
  const restrictedSelectedKeys = selectedVaccineKeys.filter(
    (vax) => vax in pregnancyRestrictedVaccines
  );
  const nonRestrictedSelectedKeys = selectedVaccineKeys.filter(
    (vax) => !(vax in pregnancyRestrictedVaccines)
  );

  const restrictedSelections = Object.keys(pregnancyRestrictedVaccines).reduce((cur, key) => {
    if (selectedVaccines[key]) {
      return Object.assign(cur, { [key]: selectedVaccines[key] });
    }
    return cur;
  }, {});
  const restrictedSelectionsWithoutRsv = Object.keys(restrictedSelections).reduce((cur, key) => {
    if (key !== 'RSV') {
      return Object.assign(cur, { [key]: restrictedSelections[key] });
    }
    return cur;
  }, {});

  const isRsvSelected = restrictedSelectedKeys.includes('RSV');
  const isAllRestricted = selectedVaccineKeys.length > 0 && nonRestrictedSelectedKeys.length === 0;
  const hasNonRsvRestricted = Object.keys(restrictedSelectionsWithoutRsv).length > 0;

  const getVaccineNames = (selections) =>
    Object.values(selections).map((vaccine, index) => {
      return (
        <>
          <L10n key={vaccine.Name} tokenID={`vaccine.${vaccine.Label__c}`} />
          {index === Object.keys(selections).length - 2 && Object.keys(selections).length > 2
            ? `, ${intl.formatMessage({ id: 'general.and' })} `
            : index === Object.keys(selections).length - 2
            ? ` ${intl.formatMessage({ id: 'general.and' })} `
            : index < Object.keys(selections).length - 1
            ? ', '
            : ''}
        </>
      );
    });

  const vaccineNames = getVaccineNames(restrictedSelectionsWithoutRsv);
  // const vaccineNamesWithoutRsv = getVaccineNames(restrictedSelectionsWithoutRsv);

  const restrictedInfoByte = {
    info: (
      <Typography variant="primaryMedium">
        <L10n
          tokenID="page.eligibility.pregnancy_warning"
          values={{ vaccines: Object.keys(restrictedSelectionsWithoutRsv).length, vaccineNames }}
        />
      </Typography>
    ),
    variant: 'warning',
  };

  const rsvInfoByte = {
    info: (
      <Typography variant="primaryMedium">
        <L10n tokenID="page.eligibility.RSV_info" />
      </Typography>
    ),
    variant: 'warning',
  };

  const goToDobPage = () => {
    updateUiState('currentPage', 0);
    updateApptState('products', {});
  };

  const buildAllRestrictedInfoByte = (selections, names, variant = 'error') => ({
    info: (
      <Typography tag="p" variant="primaryMedium">
        <L10n
          tokenID="page.eligibility.pregnancy_error"
          values={{
            vaccines: Object.keys(selections).length,
            vaccineNames: names,
            link: (
              <Button handleClick={goToDobPage} size="noPadding" styling="transparent">
                <Typography tag="p" variant="underlineMedium" color={{ color: 'highlight' }}>
                  <L10n tokenID="page.eligibility.link" />
                </Typography>
              </Button>
            ),
          }}
        />
      </Typography>
    ),
    variant,
  });

  const allRestrictedInfoByte = buildAllRestrictedInfoByte(restrictedSelections, vaccineNames);
  // const allRestrictedWithoutRsvInfoByte = buildAllRestrictedInfoByte(
  //   restrictedSelectionsWithoutRsv,
  //   vaccineNamesWithoutRsv,
  //   'warning'
  // );

  const infoByte = () => {
    const { hasRestricted, allRestricted } = restrictedVaccinesSelected();
    if (!hasRestricted) return null;
    if (isRsvSelected) {
      if (hasNonRsvRestricted) {
        return {
          info: [
            {
              info: restrictedInfoByte.info,
              variant: restrictedInfoByte.variant,
            },
            {
              info: rsvInfoByte.info,
              variant: rsvInfoByte.variant,
            },
          ],
        };
      }
      return rsvInfoByte;
    }
    if (allRestricted) {
      return allRestrictedInfoByte;
    }
    return restrictedInfoByte;
  };

  const radioOptions = {
    uniqueId: 'radio1',
    choices: [
      {
        label: <L10n tokenID="general.yes" />,
        value: 'yes',
        info: infoByte()?.info,
        infoVariant: infoByte()?.variant,
      },
      {
        label: <L10n tokenID="general.no" />,
        value: 'no',
      },
    ],
  };

  const validateInput = (value, error = <L10n tokenID="page.eligibility.required" />) => {
    return (!value && { hasError: true, error }) || { hasError: false, error: null };
  };

  const handleRadioChange = (event) => {
    const { value } = event.target.value ? event.target : event.target.dataset;
    const { hasError, error } = validateInput(value);
    setInputState({
      ...inputState,
      isPregnant: {
        ...inputState.isPregnant,
        value,
        hasError,
        error,
      },
    });
    updatePatientStore('isPregnant', value);
  };

  const getEligibleVaccines = () => {
    const eligibleVaccines = removeSpecificVaccines(selectedVaccines, null, restrictedSelections);
    return eligibleVaccines;
  };

  const swapRsvVaccine = (vaccines, isPregnant) => {
    if (!vaccines.RSV) return vaccines;

    const updatedVaccines = { ...vaccines };
    const rsvData = updatedVaccines.RSV;

    // Get the appropriate manufacturer from the manufacturers object
    if (rsvData.manufacturers) {
      const selectedManufacturer = isPregnant
        ? rsvData.manufacturers['RSV Abrysvo Pfizer']
        : rsvData.manufacturers['RSV Arexvy GSK (not for pregnant)'];

      if (selectedManufacturer) {
        // Keep the RSV group structure with selected property (like COVID vaccines)
        updatedVaccines.RSV = {
          ...rsvData,
          selected: selectedManufacturer,
        };
      }
    }

    return updatedVaccines;
  };

  const handleNext = () => {
    const { hasError, error } = validateInput(inputState.isPregnant.value);
    setInputState({
      ...inputState,
      isPregnant: {
        ...inputState.isPregnant,
        hasError,
        error,
      },
    });
    if (!hasError) {
      // Swap RSV vaccine based on pregnancy status before filtering
      const vaccinesWithRsvSwapped = swapRsvVaccine(
        selectedVaccines,
        inputState.isPregnant.value === 'yes'
      );
      const eligibleVaccines =
        inputState.isPregnant.value === 'yes'
          ? removeSpecificVaccines(vaccinesWithRsvSwapped, null, restrictedSelectionsWithoutRsv)
          : vaccinesWithRsvSwapped;
      if (Object.keys(eligibleVaccines).length > 0) updateApptState('products', eligibleVaccines);
      updateUiState('validPage', true);
    }
  };

  return (
    <>
      <RedirectHandler pageNumber={2} />
      <Box
        data-testid="isPregnant"
        gridColumn={['1/5', '1 / 13', 'auto /span 13']}
        display="grid"
        gridGap="xLarge"
      >
        <Box display="grid" gridGap="large">
          <Typography tag="h1" variant={['primaryXLarge', 'primaryWeb3']}>
            <L10n tokenID="page.eligibility.header" />
          </Typography>
          <Typography tag="p" variant={['primaryBase']}>
            <L10n tokenID="page.eligibility.subheader" />
          </Typography>
        </Box>
        <Box display="grid" gridGap="xLarge">
          <RadioGroup
            label={<L10n tokenID="page.eligibility.pregnant" />}
            options={radioOptions}
            onChange={handleRadioChange}
            selectedOption={inputState.isPregnant.value}
            name="isPregnant"
            error={inputState.isPregnant.error}
          />
        </Box>
        <PageNavigation
          handleNext={handleNext}
          disableNext={
            Object.keys(getEligibleVaccines()).length <= 0 &&
            inputState.isPregnant.value === 'yes' &&
            !(isAllRestricted && isRsvSelected)
          }
        />
      </Box>
    </>
  );
};

export default IsPregnant;
